import React, { memo, useCallback, useMemo } from 'react';
import { StyleSheet, View, } from 'react-native';
import Animated from 'react-native-reanimated';
import { FlatList } from 'react-native-gesture-handler';
import { CollapsableSettingsSection, rem, SCROLL_INDICATOR_INSETS, } from 'design-system';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { useDefaultHeaderHeight } from 'hooks/useHeaderHeight';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useAppSelector } from 'store/hooks';
import { formatAmount, formatDateForItem } from 'utils';
import DiscountBanner from 'features/promotions/components/DiscountBanner';
import AnimatedWiggleCard from 'features/settings/plan-overview/components/AnimatedWiggleCard';
import { selectDefaultCurrency } from 'reducers/selectors';
import i18n from 'utils/i18n';
import { tierThemes } from '../benefits';
import { Tier, tierGreaterThan, tierLessThan } from '../entities';
import { selectAnnualDiscountPercentage, selectFilteredBenefitsDataForTierWithCustomAndTopFeatures, selectShouldShowStripe, } from '../selectors';
import { keepYearlyText, switchToYearlyText } from '../annualDiscount';
import { formatMonthlyPriceFromYearly } from '../trial/utils';
import SubscriptionCard from './SubscriptionCard';
import TierBenefit from './TierBenefit';
import TierFlatListFooter from './TierFlatListFooter';
import { selectIsUserOnPremium, selectUserTier } from '../selectors/tier';
const AnimatedFlatList = Animated.createAnimatedComponent(FlatList);
const keyExtractor = (item) => item.name;
const PADDING_TOP = rem(120);
const buttonReversed = { brandReversed: true };
const TierFlatList = ({ scrollRef, isActive, tier, localizedPrice, yearlyPrice, currency, hasButton, contentContainerStyle, onScroll, onPress, onPressBenefit, isFetching, showSelector = true, }) => {
    const emmaProStatus = useAppSelector((store) => store.user.user.emmaProStatus);
    const userTier = useAppSelector(selectUserTier);
    const userCurrency = useAppSelector(selectDefaultCurrency);
    const headerHeight = useDefaultHeaderHeight();
    const benefitsData = useAppSelector((store) => selectFilteredBenefitsDataForTierWithCustomAndTopFeatures(store, tier));
    const renderitem = useCallback(({ item: category }) => {
        const { data } = category;
        if (data.length === 0) {
            return null;
        }
        return (<CollapsableSettingsSection collapsed={false} key={category.name} title={category.name} enabled={category.collapsible === undefined || category.collapsible === true}>
          {data.map((benefit) => {
                const { customName, customDescription, customIcon } = benefit.customData || {};
                return (<TierBenefit key={benefit.id} item={benefit} active={isActive} onPressBenefit={onPressBenefit} customName={customName} customDescription={customDescription} customIcon={customIcon} withArrow={benefit?.withArrow}/>);
            })}
        </CollapsableSettingsSection>);
    }, [isActive, onPressBenefit]);
    const isOnPremium = useAppSelector(selectIsUserOnPremium);
    const canUseStripe = useAppSelector(selectShouldShowStripe);
    const showProrataInfoCard = isOnPremium && canUseStripe;
    const annualDiscount = useAppSelector((store) => selectAnnualDiscountPercentage(store, tier));
    const header = useMemo(() => {
        const isUpgrade = tierGreaterThan(tier, userTier);
        return (<View style={{ marginTop: rem(8), gap: rem(16) }}>
        <DiscountBanner hideClose showForTier={tier}/>
        {isActive ? null : (<SubscriptionCard tier={tier} price={tier === Tier.free ? formatAmount(0, currency) : localizedPrice} yearlyPrice={yearlyPrice && currency
                    ? formatMonthlyPriceFromYearly({
                        price: yearlyPrice,
                        currency,
                    })
                    : undefined} discount={annualDiscount ? `${annualDiscount}% OFF` : undefined} showDescription={!showProrataInfoCard} style={styles.headerCard}/>)}
        {!!emmaProStatus.isOnFreeTrial &&
                !!emmaProStatus.currentFreeTrialDays &&
                !!isUpgrade &&
                !!hasButton && (<AnimatedWiggleCard color={buttonReversed} title={`Why not ${i18n.t('maximise')} your trial?`} description={`During your ${emmaProStatus.currentFreeTrialDays}-day free trial, you can explore a higher tier plan with no commitment. Unlock ${tier === Tier.ultimate ? 'the full experience' : 'it'} now!`} buttonText={`Try ${tierThemes[tier].name} for ${formatAmount(0, emmaProStatus.currentPrice?.currency || userCurrency, 2)}`} onPress={onPress} delay={1000} hideArrow disableFade isFetching={isFetching} cardStyle={styles.headerCard}/>)}
        {!!showProrataInfoCard &&
                (!!isUpgrade || (isActive && emmaProStatus.period === 'monthly')) &&
                !!hasButton ? (<AnimatedWiggleCard color={buttonReversed} title="Upgrade for less" description={`We’ll apply a credit for your remaining ${tierThemes[userTier].name} ${emmaProStatus.period} time toward your new plan. You’ll only pay the difference`} buttonText={isActive && emmaProStatus.period === 'monthly'
                    ? switchToYearlyText(annualDiscount)
                    : 'Continue'} onPress={onPress} delay={1000} hideArrow disableFade isFetching={isFetching} cardStyle={styles.headerCard}/>) : null}
        {isActive &&
                emmaProStatus.period === 'yearly' &&
                emmaProStatus.autoRenewPeriod === 'monthly' &&
                userTier === emmaProStatus.autoRenewTier &&
                !!hasButton ? (<AnimatedWiggleCard color={buttonReversed} title="Keep your discount" description={`You’re switching to a monthly plan. Stay on the yearly plan to keep ${annualDiscount
                    ? `the ${annualDiscount}% discount`
                    : 'your annual discount'}`} buttonText={keepYearlyText(annualDiscount)} onPress={onPress} delay={1000} hideArrow disableFade isFetching={isFetching} cardStyle={styles.headerCard}/>) : null}
        {isActive &&
                emmaProStatus.autoRenewTier &&
                emmaProStatus.expiresOn &&
                emmaProStatus.period === 'yearly' &&
                tierLessThan(emmaProStatus.autoRenewTier, userTier) &&
                !!hasButton ? (<AnimatedWiggleCard color={buttonReversed} title={`Keep your ${tierThemes[tier].name}`} description={`Your plan is scheduled to renew to ${formatDateForItem(emmaProStatus.expiresOn, tierThemes[emmaProStatus.autoRenewTier].name)}. Stay on ${tierThemes[tier].name} to keep enjoying your exclusive benefits`} buttonText={`Keep ${tierThemes[tier].name}`} onPress={onPress} delay={1000} hideArrow disableFade isFetching={isFetching} cardStyle={styles.headerCard}/>) : null}
      </View>);
    }, [
        annualDiscount,
        currency,
        emmaProStatus.autoRenewPeriod,
        emmaProStatus.autoRenewTier,
        emmaProStatus.currentFreeTrialDays,
        emmaProStatus.currentPrice?.currency,
        emmaProStatus.expiresOn,
        emmaProStatus.isOnFreeTrial,
        emmaProStatus.period,
        hasButton,
        isActive,
        isFetching,
        localizedPrice,
        onPress,
        showProrataInfoCard,
        tier,
        userCurrency,
        userTier,
        yearlyPrice,
    ]);
    const renderFooter = useMemo(() => <TierFlatListFooter />, []);
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING);
    const paddingTop = headerHeight + PADDING_TOP - (showSelector ? 0 : rem(69));
    return (<AnimatedFlatList ref={scrollRef} data={benefitsData} renderItem={renderitem} ListHeaderComponent={header} ListFooterComponent={renderFooter} scrollEventThrottle={16} initialNumToRender={2} keyExtractor={keyExtractor} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} onScroll={onScroll} contentContainerStyle={[
            { paddingTop },
            paddingBottom,
            contentContainerStyle,
        ]}/>);
};
const styles = StyleSheet.create({
    headerCard: {
        marginTop: 0,
        marginBottom: 0,
    },
});
export default memo(TierFlatList);
